import { useTranslation } from '@/hooks/useTranslation';

interface SearchRangeBarProps {
  /** Number of images in the chronological sequence. */
  total: number;
  /** Highest index confirmed OK — everything in `0..lo` is settled. */
  lo: number;
  /** Lowest index confirmed worn — everything in `hi..total - 1` is settled. */
  hi: number;
  /** Index currently being asked about, or `null` when no question is pending. */
  current: number | null;
}

/** Diagonal hatching that reads as "still unknown" without competing with the solid segments. */
const HATCH_PATTERN =
  'repeating-linear-gradient(45deg, transparent 0 4px, color-mix(in oklab, var(--color-muted-foreground) 40%, transparent) 4px 6px)';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Percentage string with float noise trimmed, e.g. `30%`, `6.25%`. */
const toPercent = (ratio: number): string => `${Number((ratio * 100).toFixed(4))}%`;

/**
 * Horizontal bracket showing how far the dichotomous search has converged: the confirmed-OK zone
 * grows from the left, the confirmed-worn zone from the right, and the hatched window between them
 * shrinks with every answer. Purely presentational — every value comes from props.
 */
export function SearchRangeBar({ total, lo, hi, current }: SearchRangeBarProps) {
  const { t } = useTranslation();
  const label = t('labeling.dichotomous.compare.rangeLabel');

  if (total <= 0) {
    return (
      <div
        role="img"
        aria-label={label}
        className="h-2.5 w-full rounded-full border border-border bg-muted"
      />
    );
  }

  const okCount = clamp(lo + 1, 0, total);
  const wornCount = clamp(total - hi, 0, total);
  const unknownCount = Math.max(0, total - okCount - wornCount);

  const okWidth = toPercent(okCount / total);
  const wornWidth = toPercent(wornCount / total);
  const unknownWidth = toPercent(unknownCount / total);
  const unknownOffset = toPercent(okCount / total);

  const markerLeft =
    current === null
      ? null
      : toPercent(total === 1 ? 0 : clamp(current, 0, total - 1) / (total - 1));

  return (
    <div role="img" aria-label={label} className="relative h-2.5 w-full">
      <div className="absolute inset-0 overflow-hidden rounded-full border border-border bg-muted">
        {okCount > 0 && (
          <div
            data-testid="range-segment-ok"
            className="absolute inset-y-0 bg-ok"
            style={{ left: '0%', width: okWidth }}
          />
        )}
        {unknownCount > 0 && (
          <div
            data-testid="range-segment-unknown"
            className="absolute inset-y-0 bg-muted"
            style={{ left: unknownOffset, width: unknownWidth, backgroundImage: HATCH_PATTERN }}
          />
        )}
        {wornCount > 0 && (
          <div
            data-testid="range-segment-worn"
            className="absolute inset-y-0 bg-worn"
            style={{ right: '0%', width: wornWidth }}
          />
        )}
      </div>
      {markerLeft !== null && (
        <div
          data-testid="range-marker"
          className="absolute top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground shadow-sm motion-safe:animate-pulse"
          style={{ left: markerLeft }}
        />
      )}
    </div>
  );
}
